'use client';

import { useEffect, useRef, useState } from 'react';

const stats = [
  { value: 5000, suffix: '+', label: 'Businesses Registered', sub: 'Across India' },
  { value: 98, suffix: '%', label: 'Client Satisfaction', sub: 'Rated by our clients' },
  { value: 15, suffix: '+', label: 'Years of Experience', sub: 'Industry expertise' },
  { value: 200, suffix: '+', label: 'Expert Professionals', sub: 'On our team' },
];

function useCountUp(target: number, duration = 1800, start = false) {
  const [count, setCount] = useState(0);
  useEffect(() => {
    if (!start) return;
    let startTime: number | null = null;
    const step = (timestamp: number) => {
      if (!startTime) startTime = timestamp;
      const progress = Math.min((timestamp - startTime) / duration, 1);
      const ease = 1 - Math.pow(1 - progress, 3);
      setCount(Math.floor(ease * target));
      if (progress < 1) requestAnimationFrame(step);
    };
    requestAnimationFrame(step);
  }, [start, target, duration]);
  return count;
}

function StatItem({ stat, trigger }: { stat: typeof stats[0]; trigger: boolean }) {
  const count = useCountUp(stat.value, 1600, trigger);
  return (
    <div className="flex flex-col items-center text-center px-6">
      <div
        className="text-4xl lg:text-5xl font-bold mb-1"
        style={{
          fontFamily: "'Sora', sans-serif",
          background: 'linear-gradient(135deg, #d97706, #f59e0b)',
          WebkitBackgroundClip: 'text',
          WebkitTextFillColor: 'transparent',
        }}
      >
        {count}{stat.suffix}
      </div>
      <div className="text-slate-900 font-semibold text-sm mb-0.5">{stat.label}</div>
      <div className="text-slate-400 text-xs">{stat.sub}</div>
    </div>
  );
}

export function StatsSection() {
  const ref = useRef<HTMLDivElement>(null);
  const [triggered, setTriggered] = useState(false);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setTriggered(true);
          observer.disconnect();
        }
      },
      { threshold: 0.3 }
    );
    if (ref.current) observer.observe(ref.current);
    return () => observer.disconnect();
  }, []);

  return (
    <section ref={ref} className="bg-slate-950 py-8 relative overflow-hidden">
      {/* Subtle top accent */}
      <div className="absolute top-0 left-0 right-0 h-px bg-gradient-to-r from-transparent via-amber-500/60 to-transparent" />
      <div className="absolute bottom-0 left-0 right-0 h-px bg-gradient-to-r from-transparent via-amber-500/60 to-transparent" />

      <div className="max-w-7xl mx-auto px-6 lg:px-12">
        <div className="grid grid-cols-2 lg:grid-cols-4 divide-x divide-y lg:divide-y-0 divide-white/10">
          {stats.map((stat) => (
            <StatItem key={stat.label} stat={stat} trigger={triggered} />
          ))}
        </div>
      </div>
    </section>
  );
}